import React, { useState, useEffect } from 'react';
import RumbleApiHelp from './RumbleApiHelp';
import SettingsSkeleton from './SettingsSkeleton';
import EventSettings from './EventSettings';
import { useAuth } from '../contexts/AuthContext';
import {
  validateRumbleApiKey,
  saveRumbleSettings,
  loadRumbleSettings,
  deleteRumbleSettings
} from '../lib/rumbleApi';

interface SettingsProps {
  isConnected: boolean;
  onSaveSettings: () => void;
}

const Settings: React.FC<SettingsProps> = ({ onSaveSettings }) => {
  const [showApiHelp, setShowApiHelp] = useState(false);
  const [rumbleApiUrl, setRumbleApiUrl] = useState('');
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingConnection, setLoadingConnection] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [rumbleUsername, setRumbleUsername] = useState<string | null>(null);

  const { user } = useAuth();

  // Check if URL is in correct format
  const isValidUrlFormat = rumbleApiUrl.startsWith('https://rumble.com/-livestream-api/get-data?key=') && 
                          rumbleApiUrl.length > 50 && 
                          !rumbleApiUrl.includes('••••');

  // Load existing settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      setLoadingConnection(true);
      try {
        const settings = await loadRumbleSettings();
        // Only consider connected if api_key and rumble_user_id are not null
        if (settings && settings.api_key && settings.rumble_user_id) {
          setIsConnected(true);
          setRumbleUsername(settings.rumble_username);
          // Don't show the actual API key for security
          setRumbleApiUrl('https://rumble.com/-livestream-api/get-data?key=••••••••');
        } else {
          setIsConnected(false);
          setRumbleApiUrl('');
          setRumbleUsername(null);
        }
      } catch (err) {
        console.error('Error loading settings:', err);
        setIsConnected(false);
      } finally {
        setLoadingConnection(false);
      }
    };

    if (user) {
      loadSettings();
    } else {
      setLoadingConnection(false);
      setIsConnected(false);
    }
  }, [user]);

  const handleConnect = async () => {
    if (!user) {
      setError('You must be logged in to connect');
      return;
    }

    if (!isValidUrlFormat) {
      setError('Please enter a valid Rumble API URL');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      // Validate the API key
      const rumbleData = await validateRumbleApiKey(rumbleApiUrl);
      
      // Save to Supabase
      await saveRumbleSettings(rumbleApiUrl, rumbleData);
      
      setIsConnected(true);
      setRumbleUsername(rumbleData.username);
      setSuccess(`Successfully connected to Rumble as @${rumbleData.username}`);
      
      // Clear the URL and show masked version for security
      setRumbleApiUrl('https://rumble.com/-livestream-api/get-data?key=••••••••');
      
      // Call parent save handler
      onSaveSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect');
      setIsConnected(false);
    } finally {
      setLoading(false);
    }
  };

  const handleDisconnect = async () => {
    if (!user) {
      return;
    }

    try {
      await deleteRumbleSettings();
      setIsConnected(false);
      setRumbleApiUrl('');
      setRumbleUsername(null);
      setSuccess('Disconnected from Rumble. Enter a new API URL to reconnect.');
    } catch (err) {
      setError('Failed to disconnect. Please try again.');
    }
  };

  // Show skeleton while initial loading
  if (loadingConnection) {
    return <SettingsSkeleton />;
  }

  return (
    <section id="settings" className="content-section">
      <div className="flex justify-between items-center mb-6 header-border pb-4">
        <h2 className="text-xl font-semibold text-white">settings</h2>
      </div>

      {error && (
        <div className="mb-4 p-3 border-2 border-red-500 text-red-400">
          <i className="fa-solid fa-exclamation-circle mr-2"></i>
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 border-2 border-green-500 text-green-400">
          <i className="fa-solid fa-check-circle mr-2"></i>
          {success}
        </div>
      )}

      {!user && (
        <div className="mb-4 p-3 border-2 border-yellow-500 text-yellow-400">
          <i className="fa-solid fa-info-circle mr-2"></i>
          Please sign in to save your settings
        </div>
      )}
      
      <div className="space-y-6">
        <div className="paint-card p-4">
          <h3 className="text-lg font-medium mb-4 text-white border-b-2 border-white pb-2">
            rumble connection
          </h3>
          <div className="space-y-4">
            <div>
              <label htmlFor="rumbleApiUrl" className="paint-label flex items-center gap-2">
                Rumble Livestream API URL:
                <button
                  type="button"
                  onClick={() => setShowApiHelp(true)}
                  className="text-gray-400 hover:text-white transition-colors"
                  title="How to get your Rumble API URL"
                >
                  <i className="fa-solid fa-question-circle"></i>
                </button>
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  id="rumbleApiUrl"
                  name="rumbleApiUrl"
                  className="paint-input flex-1"
                  value={rumbleApiUrl}
                  onChange={(e) => setRumbleApiUrl(e.target.value)}
                  placeholder="https://rumble.com/-livestream-api/get-data?key=..."
                  disabled={loading}
                />
                {isValidUrlFormat && !isConnected && (
                  <button
                    onClick={handleConnect}
                    className="paint-button paint-button-sm"
                    disabled={loading || !user}
                  >
                    {loading ? (
                      <>
                        <i className="fa-solid fa-spinner fa-spin mr-1"></i>
                        Connecting...
                      </>
                    ) : (
                      <>
                        <i className="fa-solid fa-link mr-1"></i>
                        Connect
                      </>
                    )}
                  </button>
                )}
              </div>
              {isConnected && rumbleApiUrl.includes('••••') && (
                <p className="text-xs text-gray-400 mt-1">
                  API key is securely stored. Enter a new URL to update.
                </p>
              )}
            </div>
            <div>
              <label className="paint-label">status:</label>
              <div className={`flex items-center gap-3 p-3 border-2 ${isConnected ? 'border-green-500 bg-green-500 bg-opacity-10' : 'border-red-500 bg-red-500 bg-opacity-10'}`}>
                {loadingConnection ? (
                  <span className="text-gray-400">
                    <i className="fa-solid fa-spinner fa-spin mr-2"></i>
                    checking connection...
                  </span>
                ) : isConnected ? (
                  <div className="flex items-center justify-between w-full">
                    <span className="text-green-400">
                      <i className="fa-solid fa-check-circle mr-2"></i>
                      connected{rumbleUsername && ` (@${rumbleUsername})`}
                    </span>
                    <button
                      onClick={handleDisconnect}
                      className="text-xs text-gray-400 hover:text-white transition-colors"
                      title="Disconnect"
                    >
                      <i className="fa-solid fa-unlink"></i>
                    </button>
                  </div>
                ) : (
                  <span className="text-red-400">
                    <i className="fa-solid fa-times-circle mr-2"></i>
                    disconnected
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        <EventSettings />
      </div>
      
      <RumbleApiHelp 
        isOpen={showApiHelp}
        onClose={() => setShowApiHelp(false)}
      />
    </section>
  );
};

export default Settings;